// Pure mapping for the library notification bell (Figma "8. Edge Cases" —
// Alert): workflow runs → "Needs your review" (runs parked on a human) and
// "Failed runs" entries. Unit-tested; the bell component only renders these.

import type { WorkflowRunView } from './workflowTypes'

export interface WorkflowNotification {
  runId: string
  templateId: string
  /** Workflow display name (falls back to the template id). */
  title: string
  subtitle: string
  /** ISO timestamp the run started (for the "12 min" column). */
  at: string | null
}

export interface WorkflowNotifications {
  needsReview: WorkflowNotification[]
  failedRuns: WorkflowNotification[]
}

// completed_with_unknowns is a REVIEW state: the run finished but left open
// questions a human should look at — the bell surfaces it, not clean success.
const REVIEW_STATUSES = new Set(['waiting_for_user', 'awaiting_approval', 'completed_with_unknowns'])
const FAILED_STATUSES = new Set(['failed', 'blocked', 'guardrail_stopped', 'budget_limited'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The step a parked run is waiting on, by matching the plan phase; '' if unknown. */
function waitingStepTitle(run: WorkflowRunView): string {
  const progress = isRecord(run.progress) ? run.progress : {}
  const stepId = typeof progress.clarificationStepId === 'string' ? progress.clarificationStepId : ''
  const phase = run.planPreview?.phases?.find((p) => p.id === stepId)
  return phase?.title ?? ''
}

/**
 * Map tenant runs onto bell entries, newest first. `nameById` supplies the
 * workflow display names (from the loaded library list).
 */
export function buildWorkflowNotifications(
  runs: WorkflowRunView[],
  nameById: Record<string, string>,
): WorkflowNotifications {
  const ms = (run: WorkflowRunView) => new Date(run.startedAt ?? 0).getTime()
  const sorted = [...runs].sort((a, b) => ms(b) - ms(a))
  const toEntry = (run: WorkflowRunView, subtitle: string): WorkflowNotification => ({
    runId: run.id,
    templateId: run.templateId,
    title: nameById[run.templateId] ?? run.templateId,
    subtitle,
    at: run.startedAt ?? null,
  })

  return {
    needsReview: sorted
      .filter((run) => REVIEW_STATUSES.has(run.status))
      .map((run) => {
        if (run.status === 'completed_with_unknowns') {
          return toEntry(run, 'Completed with open questions')
        }
        const step = waitingStepTitle(run)
        return toEntry(run, step ? `Waiting on you at "${step}"` : 'Waiting on your review')
      }),
    failedRuns: sorted
      .filter((run) => FAILED_STATUSES.has(run.status))
      .map((run) => toEntry(run, 'Run failed before completing')),
  }
}
